import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {SnackbarField} from '@/components/common';
import {
    getDefaultTemplateContent,
    PROGRAM_TEMPLATE_PAGES,
    ProgramTemplatePageMeta,
} from '@/pages/components/audit-program/templates/defaults';
import {normalizeTemplateContent} from '@/pages/components/audit-program/templates/rowLevel';
import {
    getProgramTemplate,
    importProgramTemplatesFromExcel,
    saveProgramTemplate,
    seedProgramTemplates,
} from '@/services/auditService/auditProgramTemplateService';

type SectionFilter = 'A' | 'B' | 'C' | 'G' | 'I' | 'J' | 'K' | 'L' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'T' | 'V';

type ChecklistRow = {
    id?: string;
    kind?: string;
    num?: string;
    text?: string;
    note?: string;
    level?: number;
    [key: string]: unknown;
};

function cloneContent(value: Record<string, unknown>): Record<string, unknown> {
    return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

function ChecklistRowsEditor({
    rows,
    onChange,
}: {
    rows: ChecklistRow[];
    onChange: (rows: ChecklistRow[]) => void;
}) {
    const updateRow = (index: number, patch: Partial<ChecklistRow>) => {
        const next = rows.map((row, i) => {
            if (i !== index) return row;
            const merged = {...row, ...patch};
            // Keep kind in sync with level for procedure rows (not note / section / hksa / …).
            if (typeof patch.level === 'number' && patch.kind !== 'note') {
                const k = (merged.kind || '').toLowerCase();
                if (k === 'main' || k === 'sub' || k === '') {
                    merged.kind = patch.level >= 1 ? 'sub' : 'main';
                }
            }
            return merged;
        });
        onChange(next);
    };
    const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));
    const newRowAfter = (after: ChecklistRow | undefined): ChecklistRow => {
        const afterKind = (after?.kind || '').toLowerCase();
        if (afterKind === 'note') {
            return {
                id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                kind: 'note',
                num: '',
                text: '',
                level: 0,
            };
        }
        const level = typeof after?.level === 'number' ? after.level : 0;
        return {
            id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            kind: level >= 1 ? 'sub' : 'main',
            num: '',
            text: '',
            level,
        };
    };
    const insertBelow = (index: number) => {
        const next = [...rows];
        next.splice(index + 1, 0, newRowAfter(rows[index]));
        onChange(next);
    };
    const addRow = () => onChange([...rows, newRowAfter(rows[rows.length - 1])]);

    return (
        <Stack spacing={1.5}>
            {rows.map((row, index) => {
                const level = typeof row.level === 'number' ? row.level : 0;
                const kind = (row.kind || '').toLowerCase();
                const isNote = kind === 'note';
                const isHeading = kind === 'heading';
                const isSection = kind === 'section';
                const typeValue = isNote ? 'note' : isHeading ? 'heading' : isSection ? 'section' : String(level);
                return (
                    <Paper
                        key={row.id ?? `r-${index}`}
                        variant="outlined"
                        sx={{p: 1.5, ml: isNote || isSection ? 0 : level * 2}}
                    >
                        <Stack direction={{xs: 'column', md: 'row'}} spacing={1} alignItems="flex-start">
                            <FormControl size="small" sx={{width: {xs: '100%', md: 160}, flexShrink: 0}}>
                                <InputLabel id={`type-${index}`}>Type</InputLabel>
                                <Select
                                    labelId={`type-${index}`}
                                    label="Type"
                                    value={typeValue}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === 'note') {
                                            updateRow(index, {kind: 'note', level: 0});
                                            return;
                                        }
                                        if (v === 'heading') {
                                            // Keep indent when converting lettered stem (a)/c)) to heading
                                            const keepLevel = typeof row.level === 'number' && row.level > 0
                                                ? row.level
                                                : (kind === 'sub' ? Math.max(level, 1) : 0);
                                            updateRow(index, {kind: 'heading', level: keepLevel});
                                            return;
                                        }
                                        if (v === 'section') {
                                            updateRow(index, {kind: 'section', level: 0, num: ''});
                                            return;
                                        }
                                        const nextLevel = Number(v);
                                        updateRow(index, {
                                            level: nextLevel,
                                            kind: nextLevel >= 1 ? 'sub' : 'main',
                                        });
                                    }}
                                >
                                    <MenuItem value="0">0 · main</MenuItem>
                                    <MenuItem value="1">1 · sub</MenuItem>
                                    <MenuItem value="2">2 · nested</MenuItem>
                                    <MenuItem value="heading">Heading (no answers)</MenuItem>
                                    <MenuItem value="section">Section</MenuItem>
                                    <MenuItem value="note">Note</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="No."
                                size="small"
                                value={row.num ?? ''}
                                onChange={(e) => updateRow(index, {num: e.target.value})}
                                disabled={isNote || isSection}
                                placeholder={isNote || isSection ? '—' : undefined}
                                sx={{width: {xs: '100%', md: 90}}}
                            />
                            <TextField
                                label={isNote ? 'Note text' : isHeading ? 'Heading text' : 'General / procedure text'}
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                value={row.text ?? ''}
                                onChange={(e) => updateRow(index, {text: e.target.value})}
                            />
                            <Stack direction="row" spacing={0} sx={{flexShrink: 0}}>
                                <IconButton
                                    aria-label="Insert row below"
                                    onClick={() => insertBelow(index)}
                                    color="primary"
                                    title="Insert row below"
                                >
                                    <AddIcon/>
                                </IconButton>
                                <IconButton aria-label="Remove row" onClick={() => removeRow(index)} color="error">
                                    <DeleteOutlineIcon/>
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
            <Button startIcon={<AddIcon/>} onClick={addRow} variant="outlined" size="small" sx={{alignSelf: 'flex-start'}}>
                Add row
            </Button>
        </Stack>
    );
}

function ItemListEditor({
    label,
    items,
    onChange,
}: {
    label: string;
    items: Array<{num?: string; text?: string; kind?: string}>;
    onChange: (items: Array<{num?: string; text?: string; kind?: string}>) => void;
}) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{mb: 1}}>{label}</Typography>
            <ChecklistRowsEditor rows={items} onChange={onChange}/>
        </Box>
    );
}

function MemorandumEditor({
    content,
    onChange,
}: {
    content: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
}) {
    const setField = (key: string, value: unknown) => onChange({...content, [key]: value});
    const has = (key: string) => Object.prototype.hasOwnProperty.call(content, key);
    /** always=true: show even when key missing so admins can add/fix copy after a bad import. */
    const stringField = (key: string, label: string, multiline = false, always = false) => {
        if (!always && !has(key) && typeof content[key] !== 'string') return null;
        return (
            <TextField
                key={key}
                label={label}
                size="small"
                fullWidth
                multiline={multiline}
                minRows={multiline ? 3 : 1}
                value={typeof content[key] === 'string' ? (content[key] as string) : ''}
                onChange={(e) => setField(key, e.target.value)}
                sx={{mb: 1.5}}
            />
        );
    };

    return (
        <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
                Edit firm template copy only. Leave answer boxes empty — auditors fill those on the Program page.
            </Typography>
            {stringField('title', 'Title')}
            {stringField('intro', 'Intro', true)}
            {stringField('subtitle', 'Subtitle')}
            {stringField('section1Title', 'Section 1 title')}
            {stringField('confirmIntro', 'Confirm intro')}
            {Array.isArray(content.confirmItems) ? (
                <ItemListEditor
                    label="Confirm items (numbered “I confirm that…” list)"
                    items={content.confirmItems as Array<{num?: string; text?: string}>}
                    onChange={(items) => setField('confirmItems', items)}
                />
            ) : null}
            {stringField('noteAfter9', 'Note after item 9', true, true)}
            {stringField('authorizeText', 'Authorise / sign-off sentence', true, true)}
            {stringField('partnerLabel', 'Partner label', false, true)}
            {stringField('eqrLabel', 'EQR / other partner label')}
            {stringField('secondPartnerSectionTitle', 'Second partner section title', false, true)}
            {stringField('secondPartnerConfirmIntro', 'Second partner confirm intro', false, true)}
            {stringField('secondPartnerLabel', 'Second partner label', false, true)}
            {Array.isArray(content.secondPartnerItems) ? (
                <ItemListEditor
                    label="Second partner confirm items"
                    items={content.secondPartnerItems as Array<{num?: string; text?: string}>}
                    onChange={(items) => setField('secondPartnerItems', items)}
                />
            ) : null}
            {Array.isArray(content.approveItems) ? (
                <ItemListEditor
                    label="Approve items (C1)"
                    items={content.approveItems as Array<{num?: string; text?: string; kind?: string}>}
                    onChange={(items) => setField('approveItems', items)}
                />
            ) : null}
            {stringField('section2Title', 'Section 2 title (A1/C1)')}
            {Array.isArray(content.completeItems) ? (
                <ItemListEditor
                    label="Complete items (C1)"
                    items={content.completeItems as Array<{num?: string; text?: string}>}
                    onChange={(items) => setField('completeItems', items)}
                />
            ) : null}
            {stringField('section3Title', 'Section 3 title (A1)')}
            {stringField('section3DefaultText', 'Section 3 default text (A1)', true)}
            {stringField('section3RefLabel', 'Section 3 ref label (A1)')}
            {stringField('section4Title', 'Section 4 title (A1)')}
            {stringField('section4RefLabel', 'Section 4 ref label (A1)')}
            {stringField('footerNote', 'Footer note (A1)', true)}
            {stringField('teamTitle', 'Team title (C1)')}
            {stringField('teamIntro', 'Team intro (C1)', true)}
            {Array.isArray(content.followUpSections) ? (
                <Box>
                    <Typography variant="subtitle2" sx={{mb: 1}}>
                        Follow-up sections (B1: 2.1 / 3 / 4 …; defaultText seeds blank Program answers)
                    </Typography>
                    <Stack spacing={1}>
                        {(content.followUpSections as Array<{
                            key?: string;
                            title?: string;
                            defaultText?: string;
                            completedLabel?: string;
                        }>).map((sec, index) => (
                            <Paper key={sec.key ?? `fu-${index}`} variant="outlined" sx={{p: 1.5}}>
                                <Stack spacing={1}>
                                    <Stack direction={{xs: 'column', md: 'row'}} spacing={1}>
                                        <TextField
                                            label="Key (field id)"
                                            size="small"
                                            value={sec.key ?? ''}
                                            onChange={(e) => {
                                                const next = [...(content.followUpSections as Array<typeof sec>)];
                                                next[index] = {...sec, key: e.target.value};
                                                setField('followUpSections', next);
                                            }}
                                            sx={{width: {xs: '100%', md: 160}}}
                                        />
                                        <TextField
                                            label="Section title"
                                            size="small"
                                            fullWidth
                                            value={sec.title ?? ''}
                                            onChange={(e) => {
                                                const next = [...(content.followUpSections as Array<typeof sec>)];
                                                next[index] = {...sec, title: e.target.value};
                                                setField('followUpSections', next);
                                            }}
                                        />
                                        <IconButton
                                            aria-label="Remove section"
                                            color="error"
                                            onClick={() => {
                                                const next = (content.followUpSections as Array<typeof sec>)
                                                    .filter((_, i) => i !== index);
                                                setField('followUpSections', next);
                                            }}
                                        >
                                            <DeleteOutlineIcon/>
                                        </IconButton>
                                    </Stack>
                                    <TextField
                                        label="Default answer (PCL body under title)"
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={sec.defaultText ?? ''}
                                        onChange={(e) => {
                                            const next = [...(content.followUpSections as Array<typeof sec>)];
                                            next[index] = {...sec, defaultText: e.target.value};
                                            setField('followUpSections', next);
                                        }}
                                    />
                                    <TextField
                                        label="Completed label (Excel col L, e.g. B2.1 Completed - YES / NO)"
                                        size="small"
                                        fullWidth
                                        value={sec.completedLabel ?? ''}
                                        onChange={(e) => {
                                            const next = [...(content.followUpSections as Array<typeof sec>)];
                                            next[index] = {...sec, completedLabel: e.target.value};
                                            setField('followUpSections', next);
                                        }}
                                        helperText="Program shows prefix + YES/NO dropdown (same as A1)"
                                    />
                                </Stack>
                            </Paper>
                        ))}
                        <Button
                            startIcon={<AddIcon/>}
                            variant="outlined"
                            size="small"
                            sx={{alignSelf: 'flex-start'}}
                            onClick={() => {
                                const list = Array.isArray(content.followUpSections)
                                    ? [...(content.followUpSections as Array<{
                                        key?: string;
                                        title?: string;
                                        defaultText?: string;
                                        completedLabel?: string;
                                    }>)]
                                    : [];
                                list.push({key: `s${list.length + 1}`, title: '', defaultText: '', completedLabel: ''});
                                setField('followUpSections', list);
                            }}
                        >
                            Add follow-up section
                        </Button>
                    </Stack>
                </Box>
            ) : null}
            {Array.isArray(content.rows) ? (
                <Box sx={{mt: 1}}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>Rows</Typography>
                    <ChecklistRowsEditor
                        rows={content.rows as ChecklistRow[]}
                        onChange={(rows) => setField('rows', rows)}
                    />
                </Box>
            ) : null}
        </Stack>
    );
}

function ChecklistEditor({
    content,
    onChange,
}: {
    content: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
}) {
    const footer = (content.footer && typeof content.footer === 'object')
        ? (content.footer as Record<string, string>)
        : null;

    return (
        <Stack spacing={2}>
            <TextField
                label="Page title"
                size="small"
                fullWidth
                value={typeof content.title === 'string' ? content.title : ''}
                onChange={(e) => onChange({...content, title: e.target.value})}
            />
            <Box>
                <Typography variant="subtitle2" sx={{mb: 1}}>Procedure rows (General column)</Typography>
                <ChecklistRowsEditor
                    rows={Array.isArray(content.rows) ? (content.rows as ChecklistRow[]) : []}
                    onChange={(rows) => onChange({...content, rows})}
                />
            </Box>
            {footer ? (
                <Paper variant="outlined" sx={{p: 1.5}}>
                    <Typography variant="subtitle2" sx={{mb: 1}}>Footer labels</Typography>
                    {(['signedLabel', 'deadlineLabel', 'assemblyCompletedLabel', 'footerNote'] as const).map((key) => (
                        <TextField
                            key={key}
                            label={key}
                            size="small"
                            fullWidth
                            multiline={key === 'footerNote'}
                            minRows={key === 'footerNote' ? 2 : 1}
                            value={footer[key] ?? ''}
                            onChange={(e) => onChange({
                                ...content,
                                footer: {...footer, [key]: e.target.value},
                            })}
                            sx={{mb: 1}}
                        />
                    ))}
                </Paper>
            ) : null}
        </Stack>
    );
}

function JsonFallbackEditor({
    content,
    onChange,
}: {
    content: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
}) {
    const [text, setText] = useState(() => JSON.stringify(content, null, 2));
    const [parseError, setParseError] = useState<string | null>(null);

    useEffect(() => {
        setText(JSON.stringify(content, null, 2));
        setParseError(null);
    }, [content]);

    return (
        <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
                Structured editor unavailable for this page type — edit JSON content directly.
            </Typography>
            <TextField
                value={text}
                onChange={(e) => {
                    const next = e.target.value;
                    setText(next);
                    try {
                        const parsed = JSON.parse(next) as Record<string, unknown>;
                        setParseError(null);
                        onChange(parsed);
                    } catch {
                        setParseError('Invalid JSON');
                    }
                }}
                multiline
                minRows={16}
                fullWidth
                error={Boolean(parseError)}
                helperText={parseError ?? ' '}
                InputProps={{sx: {fontFamily: 'monospace', fontSize: 12}}}
            />
        </Stack>
    );
}

export default function ProgramTemplateList() {
    const [section, setSection] = useState<SectionFilter>('A');
    const [pageCode, setPageCode] = useState('A1');
    const [content, setContent] = useState<Record<string, unknown>>({});
    const [version, setVersion] = useState<number | undefined>();
    const [status, setStatus] = useState('published');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [snackbar, setSnackbar] = useState<{open: boolean; message?: string; severity?: 'success' | 'error'}>({
        open: false,
    });

    const pages = useMemo(
        () => PROGRAM_TEMPLATE_PAGES.filter((p) => p.sectionCode === section),
        [section],
    );

    const meta: ProgramTemplatePageMeta | undefined = useMemo(
        () => pages.find((p) => p.pageCode === pageCode) ?? pages[0],
        [pages, pageCode],
    );

    useEffect(() => {
        if (!pages.some((p) => p.pageCode === pageCode) && pages[0]) {
            setPageCode(pages[0].pageCode);
        }
    }, [pages, pageCode]);

    const loadPage = useCallback(async (sec: string, code: string) => {
        setLoading(true);
        try {
            const vo = await getProgramTemplate(sec, code);
            const fallback = getDefaultTemplateContent(sec, code) ?? {};
            const raw = vo?.content && Object.keys(vo.content).length > 0 ? vo.content : fallback;
            setContent(normalizeTemplateContent(cloneContent(raw)));
            setVersion(vo?.version);
            setStatus(vo?.status ?? 'published');
        } catch (e: unknown) {
            const fallback = getDefaultTemplateContent(sec, code) ?? {};
            setContent(normalizeTemplateContent(cloneContent(fallback)));
            setVersion(undefined);
            setStatus('published');
            setSnackbar({
                open: true,
                severity: 'error',
                message: e instanceof Error ? e.message : 'Failed to load template (using code defaults)',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!meta) return;
        void loadPage(meta.sectionCode, meta.pageCode);
    }, [meta, loadPage]);

    const handleSave = async () => {
        if (!meta) return;
        setSaving(true);
        try {
            const toSave = normalizeTemplateContent(cloneContent(content));
            await saveProgramTemplate({
                sectionCode: meta.sectionCode,
                pageCode: meta.pageCode,
                content: toSave,
                status: 'published',
            });
            setContent(toSave);
            setSnackbar({open: true, severity: 'success', message: `Saved ${meta.pageCode}`});
            await loadPage(meta.sectionCode, meta.pageCode);
        } catch (e: unknown) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: e instanceof Error ? e.message : 'Save failed',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = () => {
        if (!meta) return;
        const fallback = getDefaultTemplateContent(meta.sectionCode, meta.pageCode) ?? {};
        setContent(cloneContent(fallback));
        setSnackbar({open: true, severity: 'success', message: 'Reset to code defaults (not saved yet)'});
    };

    const handleSeed = async (overwrite: boolean) => {
        setSeeding(true);
        try {
            const n = await seedProgramTemplates(overwrite);
            setSnackbar({
                open: true,
                severity: 'success',
                message: overwrite
                    ? `Seeded/overwrote ${n} page template(s)`
                    : `Seeded ${n} missing page template(s)`,
            });
            if (meta) await loadPage(meta.sectionCode, meta.pageCode);
        } catch (e: unknown) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: e instanceof Error ? e.message : 'Seed failed',
            });
        } finally {
            setSeeding(false);
        }
    };

    const handleUploadExcel = async (file: File | null) => {
        if (!file) return;
        setImporting(true);
        try {
            const result = await importProgramTemplatesFromExcel(file);
            const skippedHint = result.skipped > 0 ? ` · skipped ${result.skipped}` : '';
            setSnackbar({
                open: true,
                severity: 'success',
                message: `Imported ${result.imported} page template(s)${skippedHint}`,
            });
            if (meta) await loadPage(meta.sectionCode, meta.pageCode);
        } catch (e: unknown) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: e instanceof Error ? e.message : 'Excel import failed',
            });
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const editor = (() => {
        if (!meta) return null;
        if (meta.pageType === 'checklist' || Array.isArray(content.rows)) {
            return <ChecklistEditor content={content} onChange={setContent}/>;
        }
        if (meta.pageType === 'memorandum') {
            return <MemorandumEditor content={content} onChange={setContent}/>;
        }
        return <JsonFallbackEditor content={content} onChange={setContent}/>;
    })();

    return (
        <Box sx={{p: 2, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2}}>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1} justifyContent="space-between" alignItems={{md: 'center'}}>
                <Box>
                    <Typography variant="h6">Audit Program Templates</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Edit firm-level program copy (titles, intros, checklist General text). Engagement answers stay on Program pages.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                        hidden
                        onChange={(e) => void handleUploadExcel(e.target.files?.[0] ?? null)}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<CloudUploadIcon/>}
                        disabled={seeding || importing}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {importing ? 'Importing…' : 'Upload Excel'}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={seeding || importing}
                        onClick={() => void handleSeed(false)}
                    >
                        Seed missing
                    </Button>
                    <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        disabled={seeding || importing}
                        onClick={() => void handleSeed(true)}
                    >
                        Reseed overwrite
                    </Button>
                </Stack>
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} alignItems={{sm: 'center'}}>
                <FormControl size="small" sx={{minWidth: 120}}>
                    <InputLabel>Section</InputLabel>
                    <Select
                        label="Section"
                        value={section}
                        onChange={(e) => setSection(e.target.value as SectionFilter)}
                    >
                        <MenuItem value="A">A</MenuItem>
                        <MenuItem value="B">B</MenuItem>
                        <MenuItem value="C">C</MenuItem>
                        <MenuItem value="G">G</MenuItem>
                        <MenuItem value="I">I</MenuItem>
                        <MenuItem value="J">J</MenuItem>
                        <MenuItem value="K">K</MenuItem>
                        <MenuItem value="L">L</MenuItem>
                        <MenuItem value="N">N</MenuItem>
                        <MenuItem value="O">O</MenuItem>
                        <MenuItem value="P">P</MenuItem>
                        <MenuItem value="Q">Q</MenuItem>
                        <MenuItem value="R">R</MenuItem>
                        <MenuItem value="T">T</MenuItem>
                        <MenuItem value="V">V</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{minWidth: 280, flex: 1}}>
                    <InputLabel>Page</InputLabel>
                    <Select
                        label="Page"
                        value={pageCode}
                        onChange={(e) => setPageCode(e.target.value)}
                    >
                        {pages.map((p) => (
                            <MenuItem key={p.pageCode} value={p.pageCode}>
                                {p.pageCode} — {p.title} ({p.pageType})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                    {version != null ? `v${version}` : 'not in DB'} · {status}
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{p: 2, flex: 1, minHeight: 0, overflow: 'auto'}}>
                {loading ? (
                    <Typography color="text.secondary">Loading…</Typography>
                ) : (
                    editor
                )}
            </Paper>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                    startIcon={<RestartAltIcon/>}
                    onClick={handleResetDefaults}
                    disabled={loading || saving}
                >
                    Reset defaults
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    onClick={() => void handleSave()}
                    disabled={loading || saving || !meta}
                >
                    Save & publish
                </Button>
            </Stack>

            <SnackbarField
                open={snackbar.open}
                message={snackbar.message ?? ''}
                severity={snackbar.severity}
                onClose={() => setSnackbar((s) => ({...s, open: false}))}
            />
        </Box>
    );
}
